import { createClient } from '@supabase/supabase-js';
import { getCurrentTenant } from './tenant';
import { monitor } from './monitoring';
import { wsManager } from './websocket';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true
  },
});

// Create a proxy to inject organization_id into all database operations
export const supabase = new Proxy(supabaseClient, {
  get(target, prop) {
    // Only intercept database operations
    if (prop === 'from') {
      return (table: string) => {
        const query = target.from(table);
        
        // Skip organization filtering for system tables and settings
        const systemTables = [
          'tenants',
          'tenant_users', 
          'tenant_subscriptions',
          'system_settings',
          'organizations',
          'organization_members',
          'notifications',  // Skip tenant filtering for notifications
          'units',
          'message_threads',
          'message_participants',
          'messages',
          'message_attachments'
        ];

        if (systemTables.includes(table)) {
          return query;
        }

        // Add organization_id filter to select queries
        const originalSelect = query.select.bind(query);
        query.select = function(columns?: string) {
          // Skip organization_id filter for message-related tables
          if (table.startsWith('message_')) {
            return originalSelect(columns);
          }
          // Always use default organization for now
          return originalSelect(columns).eq('organization_id', '00000000-0000-0000-0000-000000000001');
        };
        
        // Add organization_id to insert operations
        const originalInsert = query.insert.bind(query);
        query.insert = function(values: any) {
          // Add organization_id to units table
          if (table === 'units') {
            if (Array.isArray(values)) {
              values = values.map(v => ({ ...v, organization_id: '00000000-0000-0000-0000-000000000001' }));
            } else {
              values = { ...values, organization_id: '00000000-0000-0000-0000-000000000001' };
            }
          }

          if (Array.isArray(values)) {
            values = values.map(v => ({ ...v, organization_id: '00000000-0000-0000-0000-000000000001' }));
          } else {
            values = { ...values, organization_id: '00000000-0000-0000-0000-000000000001' };
          }
          return originalInsert(values);
        };
        
        return query;
      };
    }
    return target[prop];
  },
});